import { Button, H2, Paragraph, Spinner, Text, XStack, YStack } from 'tamagui';
import { Screen } from '@mrrmlab/ui';
import type { TrainingStats } from '@mrrmlab/api-client';
import { useTrainingStats } from '../hooks';

const FINETUNE_TARGET_LINES = 200;

export interface TrainingStatsScreenProps {
  onBack?: () => void;
}

export function TrainingStatsContent() {
  const query = useTrainingStats();

  if (query.isPending) return <Spinner />;
  if (query.isError) {
    return <Paragraph color="$red10">Fehler: {query.error.message}</Paragraph>;
  }

  const stats = query.data;
  const progress = Math.min(stats.correctedLines / FINETUNE_TARGET_LINES, 1);
  const progressPercent = Math.round(progress * 100);
  const remaining = Math.max(FINETUNE_TARGET_LINES - stats.correctedLines, 0);

  return (
    <YStack gap="$4">
      <YStack gap="$1">
        <Text fontSize="$10" fontWeight="700">
          {stats.correctedLines}
        </Text>
        <Text fontSize="$3" opacity={0.7}>
          korrigierte Zeilen gesammelt
        </Text>
      </YStack>

      <YStack gap="$2">
        <XStack justifyContent="space-between" alignItems="baseline">
          <Text fontSize="$3" opacity={0.7}>
            Richtung Fine-Tune-Ziel
          </Text>
          <Text fontSize="$3" fontWeight="600">
            {progressPercent}%
          </Text>
        </XStack>
        <YStack
          height={10}
          borderRadius="$2"
          backgroundColor="$color4"
          overflow="hidden"
        >
          <YStack
            height={10}
            width={`${progressPercent}%`}
            backgroundColor="$color10"
          />
        </YStack>
        <Text fontSize="$2" opacity={0.6}>
          {remaining > 0
            ? `Noch ${remaining} korrigierte Zeilen bis ${FINETUNE_TARGET_LINES} (Richtwert).`
            : `Ziel erreicht — genug Daten für ein erstes Fine-Tuning.`}
        </Text>
      </YStack>

      <YStack
        gap="$2"
        padding="$3"
        borderRadius="$3"
        borderWidth={1}
        borderColor="$borderColor"
      >
        <Stat label="Sessions insgesamt" value={String(stats.totalSessions)} />
        <Stat label="Erkannte Zeilen insgesamt" value={String(stats.totalLines)} />
        <Stat
          label="Korrekturquote"
          value={
            stats.totalLines === 0
              ? '–'
              : `${Math.round((stats.correctedLines / stats.totalLines) * 100)}%`
          }
        />
        <Stat
          label="Letzte Session"
          value={formatDate(stats.lastSessionAt)}
        />
        <Stat
          label="Erste Session"
          value={formatDate(stats.oldestSessionAt)}
        />
      </YStack>
    </YStack>
  );
}

export function TrainingStatsScreen({ onBack }: TrainingStatsScreenProps) {
  return (
    <Screen>
      <XStack alignItems="center" gap="$3">
        {onBack ? (
          <Button size="$2" chromeless onPress={onBack}>
            ← Zurück
          </Button>
        ) : null}
        <H2 flex={1}>OCR-Training</H2>
      </XStack>
      <TrainingStatsContent />
    </Screen>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <XStack justifyContent="space-between">
      <Text fontSize="$3" opacity={0.7}>
        {label}
      </Text>
      <Text fontSize="$3" fontWeight="600">
        {value}
      </Text>
    </XStack>
  );
}

function formatDate(iso: TrainingStats['lastSessionAt']): string {
  if (!iso) return '–';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('de-DE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}
